// src/services/auth/jwt-auth/JWTAuthAuthProvider.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {
  FETCH_ERROR,
  FETCH_START,
  FETCH_SUCCESS,
  LOGOUT,
} from 'shared/constants/ActionTypes';
import {setUserData, clearUserData} from '../../../../redux/actions/User';
import {
  getCookie,
  clearAllAuthCookies,
  setCookie,
} from '../../../../utils/cookieUtils';
import jwtAxios, {
  setAuthToken,
  syncTimezoneProfile,
  primeLocalTimezone,
} from './index';

const JWTAuthContext = createContext();
const JWTAuthActionsContext = createContext();

export const useJWTAuth = () => useContext(JWTAuthContext);
export const useJWTAuthActions = () => useContext(JWTAuthActionsContext);

const STORAGE_KEYS = {
  TOKEN: 'token',
  REFRESH: 'refreshToken',
  PAYLOAD: 'authPayload',
};

const safeParse = (str) => {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};
const base64UrlDecode = (s) => {
  try {
    const t = s.replace(/-/g, '+').replace(/_/g, '/');
    return decodeURIComponent(
      atob(t)
        .split('')
        .map((c) => `%${('00' + c.charCodeAt(0).toString(16)).slice(-2)}`)
        .join(''),
    );
  } catch {
    return null;
  }
};
const decodeJwt = (token) => {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const json = base64UrlDecode(parts[1]);
  return json ? safeParse(json) : null;
};

// Keep only what we truly need to persist long-term
const sanitizePayload = (payload) => {
  if (!payload) return payload;
  const user = payload.user ? {...payload.user} : null;
  if (user && 'password' in user) delete user.password;
  return {...payload, user};
};

const pickAuthState = (payload) => {
  const {
    accessToken,
    refreshToken,
    access_token,
    refresh_token,
    user = null,
    data = null,
    features = {},
    featuresList = [],
    userFeatureOverrides = [],
    businessFeatureToggles = [],
    permissions = [],
  } = payload || {};

  const token = accessToken || access_token;
  const refresh = refreshToken || refresh_token;

  const claims = decodeJwt(token);
  const roles = claims?.roles ?? [];
  const tokenExp = claims?.exp ?? null;
  const bid = claims?.bid ?? user?.business_id ?? null;

  // Extract WhatsApp-specific data from JWT claims
  const whatsappRole = claims?.whatsappRole || user?.whatsapp_role;
  const serviceType = claims?.serviceType || user?.service_type;

  const business = user?.business || null;

  // Extract permissions from data.permissions (from /me endpoint) or root permissions (from login)
  const rawPermissions = data?.permissions || permissions || [];

  // Extract permission names: handle both array of strings and array of objects
  const permissionNames = Array.isArray(rawPermissions)
    ? rawPermissions.map((p) => (typeof p === 'string' ? p : p?.name || p))
    : [];

  // Extract features from data.features (from /me endpoint) or root features (from login)
  const rawFeatures =
    data?.features || user?.features || payload?.features || [];

  // Convert features array to featuresList format expected by useAbility
  const extractedFeaturesList = Array.isArray(rawFeatures)
    ? rawFeatures.map((f) => ({
        code: typeof f === 'string' ? f : f?.slug || f?.code || f,
        enabled: true,
      }))
    : [];

  // Use existing featuresList if available, otherwise use extracted features
  const finalFeaturesList =
    featuresList && featuresList.length > 0
      ? featuresList
      : extractedFeaturesList;

  // Merge WhatsApp data into user object for easy access
  const enrichedUser = user
    ? {
        ...user,
        whatsapp_role: whatsappRole || user?.whatsapp_role,
        service_type: serviceType || user?.service_type,
      }
    : null;

  return {
    user: enrichedUser,
    business,
    roles,
    features,
    featuresList: finalFeaturesList,
    userFeatureOverrides,
    businessFeatureToggles,
    permissions: permissionNames,
    accessToken: token || null,
    refreshToken: refresh || null,
    tokenExp,
    bid,
    authPayload: payload || null,
    isAuthenticated: !!user,
    isLoading: false,
    // Also expose WhatsApp data at top level
    whatsappRole,
    serviceType,
  };
};

const JWTAuthAuthProvider = ({children}) => {
  // Get user state from Redux
  const userState = useSelector((state) => state.user);
  const dispatch = useDispatch();

  // Local state for tokens and other auth data
  const [jwtData, setJWTAuthData] = useState(() => {
    // Try to restore auth state from multiple sources
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH);
    const storedPayload = safeParse(localStorage.getItem(STORAGE_KEYS.PAYLOAD));
    const cookieUserData = getCookie('user_data');

    // Use Redux state if available, otherwise try localStorage/cookies
    const userData = userState?.user || cookieUserData || storedPayload?.user;

    // Check if token is expired and needs refresh
    if (token) {
      try {
        const claims = decodeJwt(token);
        const now = Math.floor(Date.now() / 1000);
        const isExpired = claims?.exp && claims.exp < now;

        if (isExpired && refreshToken) {
          // Return loading state, will trigger refresh in useEffect
          return {
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: true,
          };
        }
      } catch (error) {
        // Token is invalid, clear it
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
        localStorage.removeItem(STORAGE_KEYS.REFRESH);
        localStorage.removeItem(STORAGE_KEYS.PAYLOAD);
      }
    }

    if (token && userData) {
      const merged = {
        ...storedPayload,
        user: userData,
        accessToken: storedPayload?.accessToken || token,
        refreshToken: storedPayload?.refreshToken || refreshToken,
      };

      const restoredState = pickAuthState(merged);

      return {
        ...restoredState,
        isLoading: true, // Will be set to false after validation
      };
    }

    return {
      user: null,
      business: null,
      roles: [],
      features: {},
      featuresList: [],
      userFeatureOverrides: [],
      businessFeatureToggles: [],
      permissions: [],
      accessToken: null,
      refreshToken: null,
      tokenExp: null,
      bid: null,
      authPayload: null,
      isAuthenticated: false,
      isLoading: true,
      whatsappRole: null,
      serviceType: null,
    };
  });

  const refreshTimerRef = useRef(null);
  const refreshingRef = useRef(null);
  const loginInProgressRef = useRef(false);
  const authCheckCompletedRef = useRef(false);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const logout = useCallback(async () => {
    clearRefreshTimer();

    // Clear refreshing promise
    refreshingRef.current = null;

    // Clear localStorage
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH);
    localStorage.removeItem(STORAGE_KEYS.PAYLOAD);

    // Clear cookies
    clearAllAuthCookies();

    // Clear Redux state
    dispatch(clearUserData());

    setAuthToken(null);
    dispatch?.({type: LOGOUT});

    setJWTAuthData({
      user: null,
      business: null,
      roles: [],
      features: {},
      featuresList: [],
      userFeatureOverrides: [],
      businessFeatureToggles: [],
      permissions: [],
      accessToken: null,
      refreshToken: null,
      tokenExp: null,
      bid: null,
      authPayload: null,
      isAuthenticated: false,
      isLoading: false,
      whatsappRole: null,
      serviceType: null,
    });
  }, [dispatch, clearRefreshTimer]);

  const scheduleProactiveRefresh = useCallback(
    (accessToken) => {
      clearRefreshTimer();
      const claims = decodeJwt(accessToken);
      const exp = claims?.exp;
      if (!exp) return;

      const nowSec = Math.floor(Date.now() / 1000);
      const delayMs = Math.max((exp - nowSec - 60) * 1000, 5000);

      refreshTimerRef.current = setTimeout(async () => {
        // Call refresh directly without circular dependency
        if (refreshingRef.current) return refreshingRef.current;
        const rt = localStorage.getItem(STORAGE_KEYS.REFRESH);
        if (!rt) {
          logout();
          return;
        }

        try {
          const {data: refreshData} = await jwtAxios.post('/api/auth/refresh', {
            refresh_token: rt,
          });

          // Handle token persistence directly
          const token = refreshData?.accessToken || refreshData?.access_token;
          const refresh =
            refreshData?.refreshToken || refreshData?.refresh_token;

          if (token) {
            localStorage.setItem(STORAGE_KEYS.TOKEN, token);
            setAuthToken(token);
          }
          if (refresh) {
            localStorage.setItem(STORAGE_KEYS.REFRESH, refresh);
          }

          // Re-fetch /me
          const {data: meData} = await jwtAxios.get('/api/auth/me');
          const merged = {
            ...meData,
            accessToken: token || localStorage.getItem(STORAGE_KEYS.TOKEN),
            refreshToken: refresh || localStorage.getItem(STORAGE_KEYS.REFRESH),
          };

          localStorage.setItem(
            STORAGE_KEYS.PAYLOAD,
            JSON.stringify(sanitizePayload(merged)),
          );
          setJWTAuthData(pickAuthState(merged));

          // Schedule next refresh
          if (token) {
            const claims = decodeJwt(token);
            const exp = claims?.exp;
            if (exp) {
              const nowSec = Math.floor(Date.now() / 1000);
              const delayMs = Math.max((exp - nowSec - 60) * 1000, 5000);
              refreshTimerRef.current = setTimeout(() => {
                scheduleProactiveRefresh(token);
              }, delayMs);
            }
          }
        } catch (error) {
          logout();
        }
      }, delayMs);
    },
    [logout, clearRefreshTimer],
  );

  const doRefresh = useCallback(async () => {
    if (refreshingRef.current) return refreshingRef.current;
    const rt = localStorage.getItem(STORAGE_KEYS.REFRESH);
    if (!rt) {
      console.error('No refresh token found in localStorage');
      throw new Error('No refresh token');
    }

    console.log('Attempting to refresh token...');
    refreshingRef.current = jwtAxios
      .post('/api/auth/refresh', {refresh_token: rt})
      .then(async ({data: refreshData}) => {
        console.log('Token refresh successful');
        // Handle token persistence directly
        const token =
          refreshData?.data?.access_token ||
          refreshData?.accessToken ||
          refreshData?.access_token;
        const refresh =
          refreshData?.data?.refresh_token ||
          refreshData?.refreshToken ||
          refreshData?.refresh_token;

        if (token) {
          localStorage.setItem(STORAGE_KEYS.TOKEN, token);
          setAuthToken(token);
          // Schedule proactive refresh directly to avoid circular dependency
          clearRefreshTimer();
          const claims = decodeJwt(token);
          const exp = claims?.exp;
          if (exp) {
            const nowSec = Math.floor(Date.now() / 1000);
            const delayMs = Math.max((exp - nowSec - 60) * 1000, 5000);
            refreshTimerRef.current = setTimeout(() => {
              scheduleProactiveRefresh(token);
            }, delayMs);
          }
        }
        if (refresh) {
          localStorage.setItem(STORAGE_KEYS.REFRESH, refresh);
        }

        // Use user data from refresh response or re-fetch /me (keeps features/permissions in sync)
        const userData = refreshData?.data?.user || refreshData?.user;
        let meData = {};

        if (userData) {
          meData = {user: userData};
        } else {
          const {data: meResponse} = await jwtAxios.get('/api/auth/me');
          meData = meResponse;
        }

        const merged = {
          ...meData,
          accessToken: token || localStorage.getItem(STORAGE_KEYS.TOKEN),
          refreshToken: refresh || localStorage.getItem(STORAGE_KEYS.REFRESH),
        };

        localStorage.setItem(
          STORAGE_KEYS.PAYLOAD,
          JSON.stringify(sanitizePayload(merged)),
        );
        setJWTAuthData(pickAuthState(merged));

        // 🔁 Sync timezone after refresh (with small delay to ensure token is set)
        setTimeout(() => {
          syncTimezoneProfile().catch(() => {});
        }, 100);

        refreshingRef.current = null;
        return merged;
      })
      .catch((e) => {
        console.error('Token refresh failed:', e.response?.data || e.message);
        refreshingRef.current = null;

        // Check if it's a refresh token error
        const errorMessage = e.response?.data?.message || e.message || '';
        if (
          errorMessage.includes('refresh token') ||
          errorMessage.includes('expired') ||
          errorMessage.includes('invalid')
        ) {
          console.log('🔄 Refresh token is invalid/expired, logging out...');
          logout();
        }

        throw e;
      });

    return refreshingRef.current;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logout]);

  const persistTokens = useCallback(
    (authData) => {
      // Extract tokens from various possible formats
      const token =
        authData?.accessToken || authData?.access_token || authData?.token;
      const refresh =
        authData?.refreshToken || authData?.refresh_token || authData?.refresh;

      if (token) {
        localStorage.setItem(STORAGE_KEYS.TOKEN, token);
        setAuthToken(token);
        scheduleProactiveRefresh(token);
      }

      if (refresh) {
        localStorage.setItem(STORAGE_KEYS.REFRESH, refresh);
      }
    },
    [scheduleProactiveRefresh],
  );

  const persistPayload = useCallback((payload) => {
    if (payload) {
      const sanitized = sanitizePayload(payload);
      const payloadString = JSON.stringify(sanitized);
      localStorage.setItem(STORAGE_KEYS.PAYLOAD, payloadString);
    }
  }, []);

  // Cross-tab sync - DISABLED to prevent language change logout issues
  // The cross-tab sync was causing logout when language changes
  // Users will need to refresh to sync auth state across tabs
  // This is a reasonable trade-off to prevent accidental logouts
  useEffect(() => {
    // Only listen for explicit token removal (logout from another tab)
    const onStorage = (e) => {
      if (
        e.key === STORAGE_KEYS.TOKEN &&
        e.newValue === null &&
        e.oldValue !== null
      ) {
        // Token was explicitly removed in another tab, logout
        logout();
      }
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [logout]);

  useEffect(() => {
    // Skip if auth check already completed (prevents double execution in StrictMode)
    if (authCheckCompletedRef.current) return;

    // Prime timezone quickly so first calls have header
    primeLocalTimezone();

    // CRITICAL: Check localStorage immediately and restore state
    const checkAndRestoreAuth = async () => {
      // Skip if login is currently in progress
      if (loginInProgressRef.current) {
        return;
      }

      const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH);
      const storedPayload = safeParse(
        localStorage.getItem(STORAGE_KEYS.PAYLOAD),
      );
      const cookieUserData = getCookie('user_data');

      if (!token) {
        setJWTAuthData((s) => ({
          ...s,
          user: null,
          business: null,
          roles: [],
          features: {},
          featuresList: [],
          userFeatureOverrides: [],
          businessFeatureToggles: [],
          permissions: [],
          accessToken: null,
          refreshToken: null,
          tokenExp: null,
          bid: null,
          authPayload: null,
          isAuthenticated: false,
          isLoading: false,
          whatsappRole: null,
          serviceType: null,
        }));
        return;
      }

      // We have a token, try to restore state immediately

      try {
        // Try to get user data from multiple sources
        const userData = storedPayload?.user || cookieUserData;

        // If we have user data, restore immediately
        if (userData) {
          const restoredState = pickAuthState({
            ...storedPayload,
            user: userData,
            accessToken: token,
            refreshToken: refreshToken,
          });
          setJWTAuthData(restoredState);
        }

        // Set token for API calls
        setAuthToken(token);
        scheduleProactiveRefresh(token);

        // Check if token is expired and needs refresh
        try {
          const claims = decodeJwt(token);
          const now = Math.floor(Date.now() / 1000);
          const isExpired = claims?.exp && claims.exp < now;

          if (isExpired && refreshToken) {
            try {
              const {data: refreshData} = await jwtAxios.post(
                '/api/auth/refresh',
                {
                  refresh_token: refreshToken,
                },
              );

              const newToken =
                refreshData?.accessToken || refreshData?.access_token;
              const newRefreshToken =
                refreshData?.refreshToken || refreshData?.refresh_token;

              if (newToken) {
                localStorage.setItem(STORAGE_KEYS.TOKEN, newToken);
                localStorage.setItem('token', newToken); // Also update the generic token key
                setAuthToken(newToken);

                if (newRefreshToken) {
                  localStorage.setItem(STORAGE_KEYS.REFRESH, newRefreshToken);
                }

                // Update state with new token
                setJWTAuthData((prev) => ({
                  ...prev,
                  accessToken: newToken,
                  refreshToken: newRefreshToken || prev.refreshToken,
                }));

                // Schedule next refresh
                scheduleProactiveRefresh(newToken);

                // Wait a moment to ensure token is set in all axios instances
                await new Promise((resolve) => setTimeout(resolve, 100));

                // Continue with /me validation using new token
                const {data} = await jwtAxios.get('/api/auth/me');

                const completeAuthData = {
                  ...data,
                  accessToken: newToken,
                  refreshToken: newRefreshToken || refreshToken,
                };

                localStorage.setItem(
                  STORAGE_KEYS.PAYLOAD,
                  JSON.stringify(sanitizePayload(completeAuthData)),
                );
                setJWTAuthData(pickAuthState(completeAuthData));
                return;
              }
            } catch (refreshError) {
              // Show user-friendly notification
              if (window.showMessage) {
                window.showMessage({
                  message: 'Your session has expired. Please log in again.',
                  variant: 'warning',
                  duration: 5000,
                });
              }

              // If refresh fails, clear auth and redirect to login
              localStorage.removeItem(STORAGE_KEYS.TOKEN);
              localStorage.removeItem(STORAGE_KEYS.REFRESH);
              localStorage.removeItem(STORAGE_KEYS.PAYLOAD);
              setJWTAuthData((prev) => ({
                ...prev,
                user: null,
                isAuthenticated: false,
                isLoading: false,
              }));
              return;
            }
          }
        } catch (tokenError) {
          // Token validation failed, will be handled by backend validation
        }

        // Validate with backend
        const response = await jwtAxios.get('/api/auth/me');
        const responseData = response.data;

        // Handle CMS backend response format: { success: true, user: {...} }
        const freshUserData = responseData.user || responseData.data;

        // Update with fresh data from backend
        const completeAuthData = {
          ...responseData,
          user: freshUserData,
          accessToken: token,
          refreshToken: refreshToken,
          timestamp: Date.now(),
        };

        persistTokens(completeAuthData);
        persistPayload(completeAuthData);

        // Update Redux with fresh user data
        if (completeAuthData.user) {
          dispatch(setUserData(completeAuthData.user));

          // Also save to cookies for backup
          setCookie('user_data', completeAuthData.user, 7);
        }

        const authState = pickAuthState(completeAuthData);
        setJWTAuthData(authState);

        // 🔁 Sync timezone after bootstrap
        setTimeout(() => {
          syncTimezoneProfile().catch(() => {});
        }, 100);
      } catch (error) {
        const status = error?.response?.status;
        const errorMessage = error?.response?.data?.error || error?.message;

        // Only logout on 401 Unauthorized - for other errors, keep user logged in with local data
        if (status === 401) {
          console.log('Token validation failed (401), logging out...');
          // Try to refresh the token first
          try {
            await doRefresh();
          } catch (refreshError) {
            // Clear localStorage and set unauthenticated state
            localStorage.removeItem(STORAGE_KEYS.TOKEN);
            localStorage.removeItem(STORAGE_KEYS.REFRESH);
            localStorage.removeItem(STORAGE_KEYS.PAYLOAD);

            setJWTAuthData((s) => ({
              ...s,
              user: null,
              business: null,
              roles: [],
              features: {},
              featuresList: [],
              userFeatureOverrides: [],
              businessFeatureToggles: [],
              permissions: [],
              accessToken: null,
              refreshToken: null,
              tokenExp: null,
              bid: null,
              authPayload: null,
              isAuthenticated: false,
              isLoading: false,
              whatsappRole: null,
              serviceType: null,
            }));
          }
        } else {
          // For network errors or other issues, keep user logged in with local data
          console.warn(
            'Token validation failed (non-401), keeping local auth state:',
            errorMessage,
          );
          setJWTAuthData((s) => ({
            ...s,
            isLoading: false,
          }));
        }
      }
    };

    checkAndRestoreAuth().finally(() => {
      authCheckCompletedRef.current = true;
    });
  }, [
    dispatch,
    doRefresh,
    persistTokens,
    persistPayload,
    logout,
    scheduleProactiveRefresh,
  ]);

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH);
    if (!token) return;
    try {
      // Cache-bust to bypass any 304 response
      const {data} = await jwtAxios.get(`/api/auth/me?_t=${Date.now()}`);
      const freshUser = data.user || data.data;
      const completeAuthData = {
        ...data,
        user: freshUser,
        accessToken: token,
        refreshToken: refreshToken,
      };
      localStorage.setItem(
        STORAGE_KEYS.PAYLOAD,
        JSON.stringify(sanitizePayload(completeAuthData)),
      );
      if (freshUser) {
        dispatch(setUserData(freshUser));
        setCookie('user_data', freshUser, 7);
      }
      setJWTAuthData(pickAuthState(completeAuthData));
    } catch (err) {
      console.warn('refreshUser failed:', err.message);
    }
  }, [dispatch]);

  const deleteAccount = useCallback(async () => {
    await jwtAxios.delete('/api/auth/me');
    await logout();
  }, [logout]);

  const signInUser = async ({email, password, rememberMe = false}) => {
    loginInProgressRef.current = true;
    dispatch({type: FETCH_START});
    try {
      const {data} = await jwtAxios.post('/api/auth/login', {
        email,
        password,
        rememberMe,
      });

      // Handle CMS backend response format: { success: true, token, user: {...} }
      const authData = data.data || data;

      // CRITICAL: Ensure we have all required data before persisting
      if (!authData) {
        throw new Error('No authentication data received from server');
      }

      // Extract and validate tokens (CMS uses 'token' field)
      const accessToken =
        authData.token || authData.accessToken || authData.access_token;
      const refreshToken =
        authData.refreshToken || authData.refresh_token || authData.refresh;

      if (!accessToken) {
        throw new Error('No access token received from server');
      }

      // Validate user data structure
      if (!authData.user) {
        throw new Error('No user data received from server');
      }

      // Ensure user has required fields
      if (!authData.user.id || !authData.user.email) {
        throw new Error('Incomplete user data received from server');
      }

      // Create complete auth data object with permissions at root level
      // CMS backend returns: { success: true, token, user: {...} }
      const completeAuthData = {
        ...authData,
        accessToken: accessToken,
        refreshToken: refreshToken || null,
        // Ensure we have the user data from CMS format
        user: authData.user,
        // Extract permissions from user object to root level for pickAuthState
        permissions: authData.user?.permissions || authData.permissions || [],
        // Ensure we have timestamps
        timestamp: Date.now(),
      };

      // Persist everything to localStorage
      persistTokens(completeAuthData);

      persistPayload(completeAuthData);

      // Update Redux state with user data
      if (completeAuthData.user) {
        dispatch(setUserData(completeAuthData.user));

        // Also save to cookies for backup
        setCookie('user_data', completeAuthData.user, 7);

        // Store WhatsApp API key if available
        if (completeAuthData.user.api_key) {
          localStorage.setItem(
            'whatsapp_api_key',
            completeAuthData.user.api_key,
          );
        }

        // Store WhatsApp user data for muraasala service
        localStorage.setItem(
          'muraasala_user',
          JSON.stringify(completeAuthData.user),
        );

        // Check if user is verified and show warning if not
        if (!completeAuthData.user.is_verified) {
          console.warn('User is not verified:', completeAuthData.user);
          // You can add additional logic here to show verification warning
        }
      }

      // Update local state
      const authState = pickAuthState(completeAuthData);
      setJWTAuthData(authState);

      dispatch({type: FETCH_SUCCESS});

      // 🔁 Sync timezone right after login (with small delay to ensure token is set)
      setTimeout(() => {
        syncTimezoneProfile().catch(() => {});
      }, 100);
    } catch (error) {
      setJWTAuthData((s) => ({...s, isAuthenticated: false, isLoading: false}));

      // Handle specific error messages
      let errorMessage = 'Something went wrong';
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      // Handle specific error cases
      if (error?.response?.status === 401) {
        if (errorMessage.includes('Account is inactive')) {
          errorMessage = 'Your account is inactive. Please contact support.';
        } else if (errorMessage.includes('Invalid credentials')) {
          errorMessage = 'Invalid email or password. Please try again.';
        }
      }

      dispatch({
        type: FETCH_ERROR,
        payload: errorMessage,
      });
    } finally {
      loginInProgressRef.current = false;
    }
  };

  const signUpUser = async ({username, email, password, business_id}) => {
    dispatch({type: FETCH_START});
    try {
      const {data} = await jwtAxios.post('/api/auth/register', {
        username,
        email_address: email, // ✅ backend expects email_address
        password,
        business_id,
      });
      persistTokens(data);
      persistPayload(data);
      setJWTAuthData(pickAuthState(data));
      dispatch({type: FETCH_SUCCESS});

      // 🔁 Sync timezone right after sign up (with small delay to ensure token is set)
      setTimeout(() => {
        syncTimezoneProfile().catch(() => {});
      }, 100);
    } catch (error) {
      setJWTAuthData((s) => ({...s, isAuthenticated: false, isLoading: false}));
      dispatch({
        type: FETCH_ERROR,
        payload: error?.response?.data?.error || 'Something went wrong',
      });
    }
  };

  return React.createElement(
    JWTAuthContext.Provider,
    {value: {...jwtData}},
    React.createElement(
      JWTAuthActionsContext.Provider,
      {value: {signUpUser, signInUser, logout, refreshUser, deleteAccount}},
      children,
    ),
  );
};

export default JWTAuthAuthProvider;

JWTAuthAuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
